import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import {
  fetchStoreDetail,
  selectStoreById,
  selectStoreErrorById,
  selectStoreStatusById,
} from "../features/stores/storesSlice";
import {
  fetchStoreSalesAnalytics,
  fetchStoreTransactionsPage,
  selectStoreSalesAnalytics,
  selectStoreSalesTransactions,
} from "../features/storeSales/storeSalesSlice";
import "./StoreSalesPage.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_SERIES,
  STATUS_COLORS,
} from "../shared/chartTheme";

function isoDate(d) {
  return new Date(d).toISOString().slice(0, 10);
}

function defaultDay() {
  return isoDate(new Date());
}

const PAYMENT_LABELS = {
  cash: "Наличные",
  card: "Карта",
  qr: "QR",
  online: "Онлайн",
  none: "—",
};

const TRANSACTION_TYPE_LABELS = {
  sale: "Продажа",
  refund: "Возврат",
};

const STATUS_LABELS = {
  inited: "Начата",
  paid: "Оплачена",
  canceled: "Отменена",
};

function deriveTransactionStatus(row) {
  if (row?.status) return row.status;
  // Fallback for old backend response without `status` in transaction list.
  // If payment type is present and not "none", transaction is most likely paid.
  if (row?.payment_type && row.payment_type !== "none") return "paid";
  return "inited";
}

function deriveCashierIdentity(row) {
  const first = (row?.cashier_first_name || "").trim();
  const last = (row?.cashier_last_name || "").trim();
  const fullName = (row?.cashier_full_name || `${first} ${last}`).trim();
  if (fullName) {
    return {
      cashier_id: row?.cashier_id ?? null,
      cashier_username: row?.cashier_username || "",
      cashier_name: fullName,
    };
  }
  const username = row?.cashier_username;
  if (username && String(username).trim()) {
    return {
      cashier_id: row?.cashier_id ?? null,
      cashier_username: String(username).trim(),
      cashier_name: String(username).trim(),
    };
  }
  if (row?.cashier_id != null) {
    return {
      cashier_id: row.cashier_id,
      cashier_username: `ID ${row.cashier_id}`,
      cashier_name: `ID ${row.cashier_id}`,
    };
  }
  return { cashier_id: null, cashier_username: "", cashier_name: "Неизвестный кассир" };
}

function formatMoney(s) {
  if (s == null || s === "") return "—";
  const n = Number(s);
  if (Number.isNaN(n)) return String(s);
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

// Количество приходит строкой с нулями («2.000») — показываем «2» и «1.5».
function formatQuantity(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value ?? "—");
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

const PRODUCTS_PREVIEW_LIMIT = 2;

/** Краткий состав чека в ячейке: пара позиций и счётчик остальных. */
function renderProductsSummary(products, expanded) {
  if (products.length === 0) return "—";

  const preview = products.slice(0, PRODUCTS_PREVIEW_LIMIT);
  const rest = products.length - preview.length;
  return (
    <div className="tx-products">
      {preview.map((p, index) => (
        <div key={`${p.product_id ?? index}`} className="tx-product">
          {p.product_name || "Без названия"}
          <span className="tx-product-qty"> ×{formatQuantity(p.quantity)}</span>
        </div>
      ))}
      {rest > 0 && <div className="tx-product-more">ещё {rest}</div>}
      <div className="tx-product-toggle">{expanded ? "Свернуть ▲" : "Подробнее ▼"}</div>
    </div>
  );
}

const CHART_TOOLTIP = {
  backgroundColor: "var(--c-card)",
  border: "1px solid var(--c-border-light)",
  borderRadius: 8,
  color: "var(--c-text)",
};

const AXIS_TICK = { fill: "var(--c-text-muted)", fontSize: 13 };

const PAYMENT_CHART_COLORS = CHART_SERIES;
const ORDER_STATUS_COLORS = {
  paid: STATUS_COLORS.success,
  canceled: STATUS_COLORS.danger,
  inited: STATUS_COLORS.warning,
};

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function TypeAmountChart({ typeBreakdown }) {
  const data = useMemo(() => {
    const rows = Array.isArray(typeBreakdown) ? typeBreakdown : [];
    return rows.map((d) => ({
      name: TRANSACTION_TYPE_LABELS[d.transaction_type] || d.transaction_type || "—",
      total_sale: Number(d.total_sale) || 0,
      abs_total_sale: Number(d.abs_total_sale) || Math.abs(Number(d.total_sale) || 0),
      count: d.count ?? 0,
    }));
  }, [typeBreakdown]);

  if (!data.length) {
    return <div className="td-muted">Нет данных за день</div>;
  }

  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" vertical={false} />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={{ stroke: "var(--c-border)" }} tickLine={{ stroke: "var(--c-border)" }} />
          <YAxis
            tick={AXIS_TICK}
            axisLine={{ stroke: "var(--c-border)" }}
            tickLine={{ stroke: "var(--c-border)" }}
            tickFormatter={formatAxisMoney}
            width={48}
          />
          <Tooltip
            contentStyle={CHART_TOOLTIP}
            labelStyle={{ color: "var(--c-text-muted)" }}
            formatter={(value, name, item) => {
              const count = item?.payload?.count;
              if (name === "Сумма (со знаком)") {
                return [formatMoney(value), count != null ? `Сумма (${count} шт.)` : "Сумма"];
              }
              return [formatMoney(value), "Оборот"];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 14 }} />
          <Bar dataKey="total_sale" name="Сумма (со знаком)" fill={CHART_SERIES[0]} radius={[4, 4, 0, 0]} />
          <Bar dataKey="abs_total_sale" name="Оборот (абс.)" fill={CHART_SERIES[2]} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatusBreakdownChart({ breakdown }) {
  const data = useMemo(() => {
    const rows = Array.isArray(breakdown) ? breakdown : [];
    return rows
      .filter((r) => (Number(r.count) || 0) > 0)
      .map((r) => ({
        status: r.status || "inited",
        name: STATUS_LABELS[r.status] || r.status || "—",
        value: Number(r.count) || 0,
        total_sale: Number(r.total_sale) || 0,
        count: r.count ?? 0,
      }));
  }, [breakdown]);

  if (!data.length) {
    return <div className="td-muted">Нет данных</div>;
  }

  return (
    <div className="chart-wrap chart-wrap--pie">
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={88}
            paddingAngle={2}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((item, i) => (
              <Cell key={String(i)} fill={ORDER_STATUS_COLORS[item.status] || PAYMENT_CHART_COLORS[i % PAYMENT_CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={CHART_TOOLTIP}
            formatter={(value, _n, item) => {
              const totalSale = item?.payload?.total_sale;
              return [String(value), totalSale != null ? `Шт. (сумма ${formatMoney(totalSale)})` : "Шт."];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 14 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

const TX_PAGE_SIZE = 50;

export default function StoreSalesPage() {
  const { storeId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : "/warehouse/stores";
  }, [location.state]);

  const defDay = useMemo(() => defaultDay(), []);
  const [selectedDate, setSelectedDate] = useState(defDay);
  const [page, setPage] = useState(1);
  // Раскрыт состав только одного чека — иначе таблица превращается в сплошную простыню.
  const [expandedTxId, setExpandedTxId] = useState(null);
  const toggleTxDetails = (id) => setExpandedTxId((current) => (current === id ? null : id));

  const store = useSelector(selectStoreById(storeId));
  const storeStatus = useSelector(selectStoreStatusById(storeId));
  const storeError = useSelector(selectStoreErrorById(storeId));

  const analytics = useSelector(selectStoreSalesAnalytics);
  const tx = useSelector(selectStoreSalesTransactions);

  useEffect(() => {
    dispatch(fetchStoreDetail({ storeId }));
  }, [dispatch, storeId]);

  useEffect(() => {
    setPage(1);
  }, [storeId, selectedDate]);

  useEffect(() => {
    if (!storeId) return;
    dispatch(fetchStoreSalesAnalytics({ storeId, startDate: selectedDate, endDate: selectedDate }));
  }, [dispatch, storeId, selectedDate]);

  useEffect(() => {
    if (!storeId) return;
    // Список перезагружается — раскрытая строка относилась к прежней выдаче.
    setExpandedTxId(null);
    dispatch(
      fetchStoreTransactionsPage({
        storeId,
        startDate: selectedDate,
        endDate: selectedDate,
        page,
        pageSize: TX_PAGE_SIZE,
      })
    );
  }, [dispatch, storeId, selectedDate, page]);

  const totalPages = Math.max(1, Math.ceil((tx.count || 0) / (tx.pageSize || TX_PAGE_SIZE)));

  useEffect(() => {
    if (tx.status === "succeeded" && page > totalPages) {
      setPage(totalPages);
    }
  }, [tx.status, page, totalPages]);

  const aData = analytics.data;
  const totals = aData?.totals;
  const fallbackTypeBreakdown = useMemo(() => {
    const sale = { transaction_type: "sale", count: 0, total_sale: "0", abs_total_sale: "0" };
    const refund = { transaction_type: "refund", count: 0, total_sale: "0", abs_total_sale: "0" };
    const rows = Array.isArray(tx.items) ? tx.items : [];
    let saleAmount = 0;
    let refundAmount = 0;
    rows.forEach((row) => {
      const amount = Number(row?.total_sale) || 0;
      const type = row?.transaction_type || (amount < 0 ? "refund" : "sale");
      if (type === "refund") {
        refund.count += 1;
        refundAmount += amount;
      } else {
        sale.count += 1;
        saleAmount += amount;
      }
    });
    sale.total_sale = String(saleAmount);
    sale.abs_total_sale = String(Math.abs(saleAmount));
    refund.total_sale = String(refundAmount);
    refund.abs_total_sale = String(Math.abs(refundAmount));
    return [sale, refund].filter((r) => r.count > 0);
  }, [tx.items]);

  const fallbackStatusBreakdown = useMemo(() => {
    const rows = Array.isArray(tx.items) ? tx.items : [];
    const map = new Map();
    rows.forEach((row) => {
      const status = deriveTransactionStatus(row);
      const amount = Number(row?.total_sale) || 0;
      const prev = map.get(status) || { status, count: 0, total_sale: "0" };
      prev.count += 1;
      prev.total_sale = String((Number(prev.total_sale) || 0) + amount);
      map.set(status, prev);
    });
    return Array.from(map.values());
  }, [tx.items]);

  const fallbackCashierBreakdown = useMemo(() => {
    const rows = Array.isArray(tx.items) ? tx.items : [];
    const map = new Map();
    rows.forEach((row) => {
      const amount = Number(row?.total_sale) || 0;
      const identity = deriveCashierIdentity(row);
      const key = identity.cashier_id != null ? `id:${identity.cashier_id}` : `name:${identity.cashier_name}`;
      const prev = map.get(key) || {
        cashier_id: identity.cashier_id,
        cashier_username: identity.cashier_username,
        cashier_full_name: identity.cashier_name,
        count: 0,
        total_sale: "0",
      };
      prev.count += 1;
      prev.total_sale = String((Number(prev.total_sale) || 0) + amount);
      map.set(key, prev);
    });
    return Array.from(map.values()).sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [tx.items]);

  const typeBreakdownData =
    Array.isArray(aData?.type_breakdown) && aData.type_breakdown.length > 0
      ? aData.type_breakdown
      : fallbackTypeBreakdown;
  const statusBreakdownData =
    Array.isArray(aData?.status_breakdown) && aData.status_breakdown.length > 0
      ? aData.status_breakdown
      : fallbackStatusBreakdown;
  const cashierBreakdownData =
    Array.isArray(aData?.cashier_breakdown) && aData.cashier_breakdown.length > 0
      ? aData.cashier_breakdown
      : fallbackCashierBreakdown;
  const netSaleValue = totals?.net_sale ?? totals?.total_sale ?? "0";
  const saleAmountValue =
    totals?.sale_amount ??
    String(typeBreakdownData.find((x) => x.transaction_type === "sale")?.total_sale ?? "0");
  const refundAmountValue =
    totals?.refund_amount ??
    String(typeBreakdownData.find((x) => x.transaction_type === "refund")?.abs_total_sale ?? "0");
  const saleCountValue =
    totals?.sale_count ?? Number(typeBreakdownData.find((x) => x.transaction_type === "sale")?.count || 0);
  const refundCountValue =
    totals?.refund_count ??
    Number(typeBreakdownData.find((x) => x.transaction_type === "refund")?.count || 0);

  return (
    <div className="store-sales-page">
      <div className="header">
        <div>
          <Link to={fromList} className="back-link">
            ← Назад
          </Link>
          <div className="title">Продажи · магазин</div>
          <div className="sub-title">
            Транзакции за выбранный день. В аналитике учитываются продажи, возвраты и отмены.
            {storeStatus === "succeeded" && store?.name ? (
              <>
                {" "}
                <span className="mono">{store.name}</span> (id {storeId}).
              </>
            ) : (
              <> ID магазина: {storeId}.</>
            )}
          </div>
        </div>
        <div className="header-actions">
          <label className="date-field">
            Дата
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              dispatch(fetchStoreSalesAnalytics({ storeId, startDate: selectedDate, endDate: selectedDate }));
              dispatch(
                fetchStoreTransactionsPage({
                  storeId,
                  startDate: selectedDate,
                  endDate: selectedDate,
                  page: 1,
                  pageSize: TX_PAGE_SIZE,
                })
              );
              setPage(1);
            }}
          >
            Применить
          </button>
          <Link to={`/warehouse/stores/${storeId}`} state={{ from: location.pathname }} className="btn">
            Остатки на складе
          </Link>
        </div>
      </div>

      {storeStatus === "failed" && storeError && <div className="error">{storeError}</div>}
      {analytics.status === "failed" && analytics.error && <div className="error">{analytics.error}</div>}
      {tx.status === "failed" && tx.error && <div className="error">{tx.error}</div>}

      <div className="body">
        {analytics.status === "succeeded" && aData && (
          <>
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-label">Чистая сумма</div>
                <div className="kpi-value mono">{formatMoney(netSaleValue)}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Продажи / Возвраты</div>
                <div className="kpi-value mono">{totals?.transaction_count ?? "—"}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Продажи</div>
                <div className="kpi-value" style={{ fontSize: 16, color: "var(--c-text)" }}>
                  {formatMoney(saleAmountValue)} ({saleCountValue} шт.)
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Возвраты</div>
                <div className="kpi-value" style={{ fontSize: 16, color: "var(--c-danger)" }}>
                  {formatMoney(refundAmountValue)} ({refundCountValue} шт.)
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Кассиры</div>
                <div className="kpi-value mono">{totals?.cashier_count ?? cashierBreakdownData.length}</div>
              </div>
            </div>

            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-title">Продажи vs возвраты</div>
                <TypeAmountChart typeBreakdown={typeBreakdownData} />
              </div>
              <div className="chart-card">
                <div className="chart-title">Статусы транзакций</div>
                <StatusBreakdownChart breakdown={statusBreakdownData} />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-title">Кассиры в магазине за день</div>
              {cashierBreakdownData.length === 0 ? (
                <div className="td-muted">Нет данных</div>
              ) : (
                <div className="cashier-list">
                  {cashierBreakdownData.map((c, idx) => (
                    <div key={`${c.cashier_id ?? c.cashier_username}-${idx}`} className="cashier-row">
                      <div className="cashier-name">
                        {c.cashier_full_name || c.cashier_username || "Неизвестный кассир"}
                      </div>
                      <div className="cashier-meta">
                        {c.count ?? 0} транз. · {formatMoney(c.total_sale)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {analytics.status === "loading" && (
          <Loading text="Загрузка аналитики…" style={{ marginBottom: 8 }} />
        )}

        <div>
          <div className="section-title">Транзакции</div>
          {tx.status === "loading" && tx.items.length === 0 && (
            <Loading text="Загрузка списка…" style={{ marginBottom: 8 }} />
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">ID</th>
                  <th className="th">Дата</th>
                  <th className="th">Товары</th>
                  <th className="th">Тип</th>
                  <th className="th">Статус</th>
                  <th className="th">Сумма</th>
                  <th className="th">Оплата</th>
                  <th className="th">Смена</th>
                </tr>
              </thead>
              <tbody>
                {tx.items.length === 0 && tx.status === "succeeded" ? (
                  <tr>
                    <td colSpan={8} className="td-muted">
                      Нет транзакций за выбранный день
                    </td>
                  </tr>
                ) : (
                  tx.items.map((row) => {
                    const products = row.product_transactions || [];
                    const expanded = expandedTxId === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className={products.length > 0 ? "row-expandable" : undefined}
                          onClick={products.length > 0 ? () => toggleTxDetails(row.id) : undefined}
                        >
                          <td className="td-mono">{row.id}</td>
                          <td className="td">{formatDateTime(row.transaction_dt)}</td>
                          <td className="td">{renderProductsSummary(products, expanded)}</td>
                          <td className="td">
                            {TRANSACTION_TYPE_LABELS[
                              row.transaction_type || ((Number(row.total_sale) || 0) < 0 ? "refund" : "sale")
                            ] ||
                              row.transaction_type ||
                              "—"}
                          </td>
                          <td className="td">
                            {STATUS_LABELS[deriveTransactionStatus(row)] || deriveTransactionStatus(row) || "—"}
                          </td>
                          <td className="td mono">{formatMoney(row.total_sale)}</td>
                          <td className="td">{PAYMENT_LABELS[row.payment_type] || row.payment_type || "—"}</td>
                          <td className="td-mono">{row.shift_id ?? "—"}</td>
                        </tr>
                        {expanded && products.length > 0 && (
                          <tr>
                            <td className="td td-details" colSpan={8}>
                              <table className="details-table">
                                <thead>
                                  <tr>
                                    <th className="details-th">Товар</th>
                                    <th className="details-th">Штрихкод</th>
                                    <th className="details-th details-num">Кол-во</th>
                                    <th className="details-th details-num">Скидка</th>
                                    <th className="details-th details-num">Сумма</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {products.map((p, index) => (
                                    <tr key={`${row.id}-${p.product_id ?? index}`}>
                                      <td className="details-td">{p.product_name || "—"}</td>
                                      <td className="details-td mono">{p.barcode || "—"}</td>
                                      <td className="details-td details-num mono">{formatQuantity(p.quantity)}</td>
                                      <td className="details-td details-num mono">
                                        {Number(p.discount) > 0 ? `−${formatMoney(p.discount)}` : "—"}
                                      </td>
                                      <td className="details-td details-num mono">{formatMoney(p.sales_price)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {tx.count > 0 && (
            <PaginationBar
              page={page}
              totalCount={tx.count}
              pageSize={tx.pageSize || TX_PAGE_SIZE}
              itemsOnPage={tx.items.length}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}
